import React from 'react';
import PropTypes from 'prop-types';
import { Input, Dropdown } from '@gasbuddy/react-components';
import Modal from '../../modal';
import Notification from '../../notification';
import Table from '../../table';
import Button from '../../button';

import { getStatusDisplayName } from '../../station-profile-tab/station-status/helper';
import { isDiffProp, getOpenStatusName } from '../helpers';

import styles from './styles.css';

const { api } = require('../../../../lib/bputils').default;

const OPEN_STATUS_OPTIONS = ['open', 'closed', 'closed_permanently'];

export default function StationEditModal({ onClose, edit, station, onSave }) {
  const [name, setName] = React.useState('');
  const [address, setAddress] = React.useState('');
  const [crossStreet, setCrossStreet] = React.useState('');
  const [city, setCity] = React.useState('');
  const [state, setState] = React.useState('');
  const [zip, setZip] = React.useState('');
  const [latitude, setLatitude] = React.useState(0);
  const [longitude, setLongitude] = React.useState(0);
  const [openStatus, setOpenStatus] = React.useState('');

  const [showConfirmDialog, setShowConfirmDialog] = React.useState(false);

  React.useEffect(() => {
    if (edit) {
      setName(edit.station.name);
      setAddress(edit.station.address);
      setCrossStreet(edit.station.cross_street);
      setCity(edit.station.city);
      setState(edit.station.state);
      setZip(edit.station.postal_code);
      setLatitude(edit.station.latitude);
      setLongitude(edit.station.longitude);
      setOpenStatus(edit.station.open_status);
    }
  }, [edit]);

  const handleOnModalClose = () => {
    onClose();
  };

  const handleOnApproveEdit = () => {
    setShowConfirmDialog(true);
  };

  const hideConfirmDialog = () => {
    setShowConfirmDialog(false);
  };

  const createStationEditRequestObject = () => {
    const newStationEdit = {
      ...edit,
      station: {
        ...edit.station,
        name,
        address,
        cross_street: crossStreet,
        city,
        state,
        postal_code: zip,
        longitude,
        latitude,
        open_status: openStatus,
      },
    };
    delete newStationEdit.station.id;
    return newStationEdit;
  };

  const submitStationEdit = async () => {
    const newEdit = createStationEditRequestObject();
    const stationId = station.id;
    await api.put({
      path: `/suggested-edits/stations/${stationId}/approve/${edit.id}`,
      data: newEdit,
      success: () => {
        Notification.success('Successfully approved this edit. You should see the updated station info shortly');
        onSave(stationId);
        hideConfirmDialog();
      },
      error: () => Notification.error('We were unable to approve this edit at this time. Please try again.'),
    });
  };

  return (
    <>
      <Modal
        title="Station Edit"
        show={!!edit}
        onClose={handleOnModalClose}
        background
        ctaButtons={[
          <Button secondary onClick={handleOnModalClose}>Cancel</Button>,
          <Button primary onClick={handleOnApproveEdit}>Approve Edit</Button>,
        ]}
      >
        <Table
          loading={false}
          tableColumns={[{ title: '' }, { title: 'New' }, { title: 'Current' }]}
        >
          {station && edit ? (
            <>
              <tr className={isDiffProp(station, edit.station, 'display_name', 'name') ? styles.affectedRow : undefined}>
                <td><b>Name</b></td>
                <td><Input size="sm" value={name} onChange={e => setName(e.currentTarget.value)} /></td>
                <td>{station.display_name}</td>
              </tr>
              <tr className={isDiffProp(station.address, edit.station, 'address') ? styles.affectedRow : undefined}>
                <td><b>Address</b></td>
                <td><Input size="sm" value={address} onChange={e => setAddress(e.currentTarget.value)} /></td>
                <td>{station.address.address}</td>
              </tr>
              <tr className={isDiffProp(station.address, edit.station, 'cross_street') ? styles.affectedRow : undefined}>
                <td><b>Cross Street</b></td>
                <td><Input size="sm" value={crossStreet} onChange={e => setCrossStreet(e.currentTarget.value)} /></td>
                <td>{station.address.cross_street}</td>
              </tr>
              <tr className={isDiffProp(station.address, edit.station, 'city') ? styles.affectedRow : undefined}>
                <td><b>City</b></td>
                <td><Input size="sm" value={city} onChange={e => setCity(e.currentTarget.value)} /></td>
                <td>{station.address.city}</td>
              </tr>
              <tr className={isDiffProp(station.address, edit.station, 'state') ? styles.affectedRow : undefined}>
                <td><b>State</b></td>
                <td><Input size="sm" value={state} onChange={e => setState(e.currentTarget.value)} /></td>
                <td>{station.address.state}</td>
              </tr>
              <tr className={isDiffProp(station.address, edit.station, 'zip', 'postal_code') ? styles.affectedRow : undefined}>
                <td><b>Zip Code</b></td>
                <td><Input size="sm" value={zip} onChange={e => setZip(e.currentTarget.value)} /></td>
                <td>{station.address.zip}</td>
              </tr>
              <tr className={isDiffProp(station, edit.station, 'latitude') ? styles.affectedRow : undefined}>
                <td><b>Latitude</b></td>
                <td><Input size="sm" value={latitude} onChange={e => setLatitude(e.currentTarget.value)} /></td>
                <td>{station.latitude}</td>
              </tr>
              <tr className={isDiffProp(station, edit.station, 'longitude') ? styles.affectedRow : undefined}>
                <td><b>Longitude</b></td>
                <td><Input size="sm" value={longitude} onChange={e => setLongitude(e.currentTarget.value)} /></td>
                <td>{station.longitude}</td>
              </tr>
              <tr className={getOpenStatusName(edit.station.open_status) !== station.open_status ? styles.affectedRow : undefined}>
                <td><b>Open Status</b></td>
                <td>
                  <div className={styles.openStatusDropdownContainer}>
                    <Dropdown
                      className={styles.openStatusDropdown}
                      size="sm"
                      options={OPEN_STATUS_OPTIONS.map(o => ({ value: o, label: getStatusDisplayName(o) }))}
                      value={openStatus}
                      onChange={e => setOpenStatus(e.currentTarget.value)}
                    />
                  </div>
                </td>
                <td>{getStatusDisplayName(station.open_status)}</td>
              </tr>
            </>
          ) : null}
        </Table>
      </Modal>
      <Modal
        title="Approve this Edit?"
        show={showConfirmDialog}
        saveButtonText="Save"
        onSave={submitStationEdit}
        ctaButtons={[
          <Button secondary onClick={hideConfirmDialog}>Cancel</Button>,
        ]}
        onClose={hideConfirmDialog}
      >
        <div style={{ textAlign: 'center', fontSize: '16px' }}>
          <p>By approving this edit all other pending edits for this station will be deleted.</p>
          <p>Are you sure you want to approve this edit?</p>
        </div>
      </Modal>
    </>

  );
}

StationEditModal.propTypes = {
  edit: PropTypes.objectOf(PropTypes.string).isRequired,
  station: PropTypes.objectOf(PropTypes.string).isRequired,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
};

StationEditModal.defaultProps = {
  onClose: () => { },
  onSave: () => { },
};
